/**
 * Formula utilities for rendering and handling LaTeX/KaTeX formulas.
 */
import katex from 'katex';

/**
 * Checks if text contains LaTeX content.
 */
export const isLatexContent = (text: string): boolean =>
	/\\[a-zA-Z]+/.test(text) || /\^[\d{]/.test(text) || /_[\d{]/.test(text);

/**
 * Sanitizes corrupted formula data that may contain broken HTML attributes and KaTeX artifacts.
 */
export const sanitizeCorruptedFormulaData = (content: string): string => {
	if (!content) return content;
	return content.replace(
		/"?\s*(?:&nbsp;|\s)*data-index=["']?\d+["']?(?:&gt;|>)\d+[\u200B\s]*/g,
		'',
	);
};

/**
 * Disables contenteditable elements in content.
 */
const disableContentEditableElements = (content: string): string => {
	if (content && content.includes('contenteditable="true"')) {
		return content.replace(/contenteditable="true"/g, 'contenteditable="false"');
	}
	return content || '';
};

/**
 * Wraps video iframes in container divs.
 */
const wrapVideoIframes = (content: string): string => {
	const iframeRegex = /<iframe[^>]*class="[^"]*ql-video[^"]*"[^>]*>([\s\S]*?)<\/iframe>/gi;
	return content.replace(iframeRegex, '<div class="video-container">$&</div>');
};

/**
 * Renders bare formulas (without $...$ delimiters) in content.
 */
const renderBareFormulas = (content: string, isEditor?: boolean): string => {
	if (!content) return content;

	const sanitizedContent = sanitizeCorruptedFormulaData(content);

	const parser = new DOMParser();
	const doc = parser.parseFromString(`<div>${sanitizedContent}</div>`, 'text/html');
	const container = doc.body?.firstChild as HTMLElement | null;

	if (!container) return content;

	const formulaRegex =
		/\\(frac|dfrac|tfrac)\s*\{[^{}]*\}\s*\{[^{}]*\}|\\sqrt\s*\{[^{}]*\}|\\[a-zA-Z]+|[A-Za-z0-9]+\s*(\^\{[^{}]+\}|\^[A-Za-z0-9]+|_\{[^{}]+\}|_[A-Za-z0-9]+)/g;

	const walker = doc.createTreeWalker(container, NodeFilter.SHOW_TEXT);
	const textNodes: Text[] = [];

	while (walker.nextNode()) {
		const node = walker.currentNode;
		if (node.nodeType === Node.TEXT_NODE) {
			textNodes.push(node as Text);
		}
	}

	let formulaIndex = 0;

	textNodes.forEach((node) => {
		const text = node.nodeValue || '';
		if (!text) return;
		const parent = node.parentElement;
		if (parent?.closest('.table-block') || parent?.closest('.katex')) return;

		formulaRegex.lastIndex = 0;
		if (!formulaRegex.test(text)) return;

		formulaRegex.lastIndex = 0;
		const renderedText = text.replace(formulaRegex, (matchText) => {
			try {
				const formula = katex.renderToString(matchText);
				const renderedFormula = isEditor
					? `<span class="ql-formula" data-value="${matchText}" data-index="${formulaIndex}">${formula}</span>`
					: formula;
				formulaIndex++;
				return renderedFormula;
			} catch {
				formulaIndex++;
				return matchText;
			}
		});

		const frag = doc.createDocumentFragment();
		const temp = doc.createElement('span');
		temp.innerHTML = renderedText;
		while (temp.firstChild) {
			frag.appendChild(temp.firstChild);
		}
		node.parentNode?.replaceChild(frag, node);
	});

	return container.innerHTML;
};

/**
 * Renders $...$ delimited formulas in content.
 */
const renderDelimitedFormulas = (content: string, isEditor?: boolean): string => {
	if (content.search(/\$(.*?)\$/g) < 0) return content;

	let formulaIndex = 0;
	return content.replace(/\$(.*?)\$/g, (_outer: string, inner: string): string => {
		let renderedFormula = '';
		try {
			const formula = katex.renderToString(inner);
			renderedFormula = isEditor
				? `<span class="ql-formula" data-value="${inner}" data-index="${formulaIndex}">${formula}</span>`
				: formula;
		} catch {
			renderedFormula = inner;
		}
		formulaIndex++;
		return renderedFormula;
	});
};

/**
 * Main function to render all formulas in content.
 */
export const renderFormulas = (content: string, isEditor?: boolean): string => {
	let formattedContent = content || '';
	if (typeof content === 'object') formattedContent = JSON.stringify(content);

	if (!isEditor) {
		formattedContent = wrapVideoIframes(formattedContent);
	}

	const preprocessed = disableContentEditableElements(formattedContent);
	const withDelimited = renderDelimitedFormulas(preprocessed, isEditor);
	return renderBareFormulas(withDelimited, isEditor);
};

/**
 * Renders simple fractions like "1/2" as KaTeX formulas.
 */
export const renderFractions = (content: string): string => {
	const res = content.match(/\d+\/\d+/g);
	res?.forEach((match: string) => {
		const numbers = match.split('/');
		const formula = katex.renderToString(`\\frac{${numbers[0]}}{${numbers[1]}}`);
		const matchRegex = new RegExp(match, 'g');
		content = content.replace(matchRegex, formula);
	});
	return content;
};

/**
 * Creates a formula span element with KaTeX rendering.
 */
export const createFormulaElement = (formulaValue: string, index: number): HTMLElement => {
	const span = document.createElement('span');
	span.className = 'ql-formula';
	span.setAttribute('data-value', formulaValue);
	span.setAttribute('data-index', String(index));
	try {
		span.innerHTML = katex.renderToString(formulaValue);
	} catch {
		span.textContent = formulaValue;
	}
	return span;
};

// =============================================================================
// Editor-specific utilities (for WYSIWYG formula editing)
// =============================================================================

interface FormulaClickResult {
	formulaData: string;
	formulaIndex: string;
	formulaSpan: HTMLElement;
}

interface FormulaMatch {
	element: Element;
	formulaValue: string;
}

/**
 * Finds all formula elements in HTML content.
 */
const findFormulasInHtml = (container: Element): FormulaMatch[] => {
	const formulas: FormulaMatch[] = [];

	container.querySelectorAll('.ql-formula[data-value]').forEach((el) => {
		const value = el.getAttribute('data-value');
		if (value) {
			formulas.push({ element: el, formulaValue: value });
		}
	});

	container.querySelectorAll('.katex').forEach((katexEl) => {
		const parent = katexEl.parentElement;
		if (parent?.classList.contains('ql-formula')) return;

		const annotation = katexEl.querySelector('annotation[encoding="application/x-tex"]');
		const value = annotation?.textContent;
		if (value && parent) {
			formulas.push({ element: parent, formulaValue: value });
		}
	});

	container.querySelectorAll('span').forEach((span) => {
		if (span.classList.contains('ql-formula') || span.querySelector('.katex')) return;

		const textContent = span.textContent || '';
		if (isLatexContent(textContent) && !span.children.length) {
			formulas.push({ element: span, formulaValue: textContent });
		}
	});

	return formulas;
};

/**
 * Extracts formula data from a clicked element in the editor.
 */
export const getFormulaFromClick = (
	targetEl: HTMLElement,
	containerEl: HTMLElement | null,
): FormulaClickResult | null => {
	if (targetEl.closest('.table')) return null;

	if (targetEl.classList.contains('ql-formula') && targetEl.dataset.value) {
		return {
			formulaData: targetEl.dataset.value,
			formulaIndex: targetEl.dataset.index || '0',
			formulaSpan: targetEl,
		};
	}

	const katexElement = targetEl.closest('.katex') as HTMLElement | null;
	if (!katexElement) return null;

	const formulaSpan = katexElement.parentElement as HTMLElement | null;
	if (!formulaSpan) return null;

	let formulaData: string | undefined = formulaSpan.dataset.value;
	if (!formulaData) {
		const annotation = katexElement.querySelector('annotation[encoding="application/x-tex"]');
		formulaData = annotation?.textContent || undefined;
	}
	if (!formulaData) return null;

	let formulaIndex = formulaSpan.dataset.index;
	if (!formulaIndex && containerEl) {
		const allKatexElements = containerEl.querySelectorAll('.katex');
		const index = Array.from(allKatexElements).indexOf(katexElement);
		formulaIndex = index >= 0 ? String(index) : '0';
	}

	return {
		formulaData,
		formulaIndex: formulaIndex || '0',
		formulaSpan,
	};
};

/**
 * Ensures the formula span has the required attributes for editing.
 */
export const normalizeFormulaSpan = (result: FormulaClickResult): void => {
	const { formulaSpan, formulaData, formulaIndex } = result;

	if (!formulaSpan.dataset.value) formulaSpan.dataset.value = formulaData;
	if (!formulaSpan.dataset.index) formulaSpan.dataset.index = formulaIndex;
	if (!formulaSpan.classList.contains('ql-formula')) formulaSpan.classList.add('ql-formula');
};

/**
 * Replaces a formula in HTML content and re-renders all formulas.
 */
export const replaceFormulaInHtml = (
	htmlContent: string,
	oldFormula: string,
	newFormula: string,
	targetIndex?: string,
): string => {
	const container = document.createElement('div');
	container.innerHTML = htmlContent;

	const formulas = findFormulasInHtml(container);
	const matchingFormulas = formulas.filter((f) => f.formulaValue === oldFormula);

	let targetFormula: FormulaMatch | null = null;
	if (matchingFormulas.length === 1) {
		targetFormula = matchingFormulas[0];
	} else if (matchingFormulas.length > 1) {
		const byIndex = matchingFormulas.find((f) => String(formulas.indexOf(f)) === targetIndex);
		targetFormula = byIndex || matchingFormulas[0];
	}

	formulas.forEach((match, idx) => {
		const formulaValue = match === targetFormula ? newFormula : match.formulaValue;
		match.element.replaceWith(createFormulaElement(formulaValue, idx));
	});

	return container.innerHTML;
};
